import type {
    AIResponse,
    AIRequest,
    AITaskStatus,
    AIAction,
    ActionType,
    AITaskRequestContext
} from './server-types';

import type { AiPromptsLibraryService } from '../core/ai-prompts-library.service';

export type {
    AIRequest,
    AIResponse,
    TPromptsTag,
    AITaskRequest,
    AITaskResponse,
    AITaskStatus,
    CommunicationProtocol,
    AIAction,
    ActionType,
    BusinessTrendsData,
    CallToActionData,
    StructuredRequest,
    StructuredResponse,
    IHBError,
    GetStructuredRequestData,
    GetStructuredResponseData,
    MarkdownData,
    StructuredRequestType,
    StructuredResponseType,
    ResponseContentMap,
    NavigationData,
    IChatInspiration,
    RedirectToSupportData,
    SelectedProjectData,
    OptionData,
    HexColor,
    DoneResponseData,
    AIRequestContext,
    AITaskRequestContext,
    AITaskListResponse,
    PriorityListData,
    TLineConfig,
    TAxisConfig,
    TChartArgsMap,
    ChartDisplayData,
    TChartType,
    TChartArgs
} from './server-types';

export { ACTION_TYPES, ERROR_TYPES, RESPONSE_TYPES } from './server-types';

export type { PromptsLibraryItem } from '../core/ai-prompts-library.adapter';

export interface TaskOptions {
    timeout?: number;
    taskKey?: string;
}

// M4: TypeScript enforcement of mutual exclusivity for getTask options
export type GetTaskOptions =
    | { taskId: string; taskKey?: never }
    | { taskKey: string; taskId?: never };

export interface ITaskRepository {
    /**
     * Initialize the task repository by restoring tasks from storage.
     * Called automatically by useHoneybookAI hook or can be called manually.
     * This method is idempotent - multiple calls are safe.
     */
    initialize(): Promise<void>;

    /**
     * Create a new task for the specified feature.
     * @param feature - The feature name for the task
     * @param requests - Initial requests for the task
     * @param options - Optional configuration
     * @param options.timeout - Timeout for task responses in milliseconds
     * @param options.taskKey - Optional taskKey for component-task associations
     */
    createTask(
        feature: string,
        requests: AIRequest[],
        options?: TaskOptions,
        context?: AITaskRequestContext
    ): Promise<AITask>;

    /**
     * Get an existing task by its ID or taskKey.
     * Will fetch from backend if not in local cache.
     * @param feature - The feature name for the task
     * @param options - Either taskId for direct lookup or taskKey for association lookup
     * @returns The task if found, null if not found
     */
    getTask(feature: string, options: GetTaskOptions): Promise<AITask | null>;
}

export interface IHoneybookAI {
    /** Get task repository instance */
    getTaskRepository(): ITaskRepository;
    /** Get action manager instance */
    getActionManager(): IActionManager;

    getAiPromptsLibraryService(): AiPromptsLibraryService;
}

export interface AITask {
    /** Unique task identifier */
    readonly id: string;

    /** Feature this task is for */
    readonly feature: string;

    /** Current task status */
    readonly status: AITaskStatus;

    /** Timeout for task responses in milliseconds */
    readonly timeout: number;

    /** Last processed response order */
    readonly lastProcessedOrder: number;

    /** Array of all responses received so far */
    readonly responses: AIResponse[];

    /** Send a request as part of this task */
    sendRequest(request: AIRequest): Promise<void>;

    /** Get response stream starting from unprocessed responses */
    responseStream(): AsyncGenerator<AIResponse, void, unknown>;
}

/**
 * Error types
 */

export class AIError extends Error {
    public readonly cause?: unknown;

    constructor(message: string, cause?: unknown) {
        super(message);
        this.cause = cause;
        this.name = 'AIError';
    }
}

export class TaskAPIError extends AIError {
    constructor(message: string, cause?: unknown) {
        super(`API call failed: ${message}`, cause);
        this.name = 'TaskAPIError';
    }
}

export class InvalidTaskStateError extends AIError {
    constructor(taskId: string, message: string) {
        super(`Task ${taskId}: ${message}`);
        this.name = 'InvalidTaskStateError';
    }
}

export class TaskValidationError extends AIError {
    constructor(taskId: string, message: string) {
        super(`Task ${taskId}: ${message}`);
        this.name = 'InvalidTaskStateError';
    }
}

export class ResponseTimeoutError extends AIError {
    constructor(message: string, cause?: unknown) {
        super(`Response timeout: ${message}`, cause);
        this.name = 'ResponseTimeoutError';
    }
}

export class UnsupportedResponseError extends AIError {
    constructor(responseType: string, typeValue: string) {
        super(`Unsupported ${responseType} type: ${typeValue}`);
        this.name = 'UnsupportedResponseTypeError';
    }
}

export interface ActionSubscription<T extends ActionType[] = ActionType[]> {
    /**
     * Optional feature to filter actions by
     */
    feature?: string;

    /**
     * Optional action types to filter by
     */
    actionTypes?: T;

    /**
     * Callback function that will be invoked when a matching action is received
     */
    onAction: (action: Extract<AIAction, { type: T[number] }>) => void;

    /**
     * Optional error handler that will be called if the onAction callback throws
     */
    onError?: (error: Error) => void;
}

export interface IActionManager {
    /**
     * Subscribe to actions, optionally filtered by feature and action types
     */
    subscribe<T extends ActionType[] = ActionType[]>(
        subscription: ActionSubscription<T>
    ): () => void;
}
